/**
 * Utility functions for subscription-related operations using UserContext
 *
 * This utility centralizes subscription data fetching logic and integrates
 * with UserContext for proper state management and authentication.
 */

import SubscriptionsAPI from "../services/SubscriptionsAPI";

/**
 * Fetches current subscription and subscription history using provided user data
 * @param {Object} user - User object from UserContext (loggedInUser or selectedUser)
 * @returns {Promise<Object>} Object containing current subscription and history data
 * @throws {Error} If user is not provided or API calls fail
 */
export const fetchUserSubscriptionData = async (user) => {
  console.log(
    "🔄 SubscriptionUtil: Starting fetchUserSubscriptionData with UserContext...",
  );

  try {
    if (!user || !user.uuid) {
      console.error(
        "❌ SubscriptionUtil: User not authenticated - no user object or UUID provided",
      );
      throw new Error(
        "User not authenticated - no user object or UUID provided",
      );
    }

    const uuid = user.uuid;
    console.log("👤 SubscriptionUtil: Retrieved UUID from UserContext:", uuid);

    // Fetch current subscription using UUID-based methods
    console.log(
      "📡 SubscriptionUtil: Fetching current subscription for UUID:",
      uuid,
    );
    const currentSubResponse =
      await SubscriptionsAPI.getCurrentSubscription(uuid);
    console.log(
      "📥 SubscriptionUtil: Current subscription API response:",
      currentSubResponse,
    );

    let currentSubscription = null;
    if (currentSubResponse && currentSubResponse.success) {
      console.log(
        "✅ SubscriptionUtil: Current subscription found:",
        currentSubResponse.data,
      );
      currentSubscription = currentSubResponse.data;
    } else {
      console.log(
        "ℹ️ SubscriptionUtil: No current subscription found or API call failed",
      );
    }

    // Fetch subscription history using UUID-based methods
    console.log(
      "📡 SubscriptionUtil: Fetching subscription history for UUID:",
      uuid,
    );
    const historyResponse =
      await SubscriptionsAPI.getSubscriptionHistoryByUuid(uuid);
    console.log(
      "📥 SubscriptionUtil: Subscription history API response:",
      historyResponse,
    );

    let subscriptionHistory = [];
    if (historyResponse && historyResponse.success) {
      console.log(
        "✅ SubscriptionUtil: Subscription history found:",
        historyResponse.data,
      );
      subscriptionHistory = historyResponse.data || [];
    } else {
      console.log(
        "ℹ️ SubscriptionUtil: No subscription history found or API call failed",
      );
    }

    console.log(
      "✅ SubscriptionUtil: fetchUserSubscriptionData completed successfully",
    );

    return {
      currentSubscription,
      subscriptionHistory,
      user, // Include user data for convenience
      success: true,
    };
  } catch (error) {
    console.error(
      "💥 SubscriptionUtil: Error fetching subscription data:",
      error,
    );
    console.error("SubscriptionUtil error details:", {
      message: error.message,
      stack: error.stack,
      name: error.name,
    });

    throw new Error(
      `Failed to load subscription information: ${error.message}`,
    );
  }
};

/**
 * Fetches current subscription data only (without history) using provided user data
 * @param {Object} user - User object from UserContext (loggedInUser or selectedUser)
 * @returns {Promise<Object|null>} Current subscription data or null if none found
 * @throws {Error} If user is not provided or API call fails
 */
export const fetchCurrentSubscription = async (user) => {
  console.log(
    "🔄 SubscriptionUtil: Starting fetchCurrentSubscription with UserContext...",
  );

  try {
    if (!user || !user.uuid) {
      console.error(
        "❌ SubscriptionUtil: User not authenticated - no user object or UUID provided",
      );
      throw new Error(
        "User not authenticated - no user object or UUID provided",
      );
    }

    const uuid = user.uuid;

    console.log(
      "📡 SubscriptionUtil: Fetching current subscription for UUID:",
      uuid,
    );
    const currentSubResponse =
      await SubscriptionsAPI.getCurrentSubscription(uuid);

    if (currentSubResponse && currentSubResponse.success) {
      console.log(
        "✅ SubscriptionUtil: Current subscription found:",
        currentSubResponse.data,
      );
      return currentSubResponse.data;
    } else {
      console.log("ℹ️ SubscriptionUtil: No current subscription found");
      return null;
    }
  } catch (error) {
    console.error(
      "💥 SubscriptionUtil: Error fetching current subscription:",
      error,
    );
    throw new Error(`Failed to load current subscription: ${error.message}`);
  }
};

/**
 * Fetches subscription history data only (without current subscription) using provided user data
 * @param {Object} user - User object from UserContext (loggedInUser or selectedUser)
 * @returns {Promise<Array>} Array of subscription history records
 * @throws {Error} If user is not provided or API call fails
 */
export const fetchSubscriptionHistory = async (user) => {
  console.log(
    "🔄 SubscriptionUtil: Starting fetchSubscriptionHistory with UserContext...",
  );

  try {
    if (!user || !user.uuid) {
      console.error(
        "❌ SubscriptionUtil: User not authenticated - no user object or UUID provided",
      );
      throw new Error(
        "User not authenticated - no user object or UUID provided",
      );
    }

    const uuid = user.uuid;

    console.log(
      "📡 SubscriptionUtil: Fetching subscription history for UUID:",
      uuid,
    );
    const historyResponse =
      await SubscriptionsAPI.getSubscriptionHistoryByUuid(uuid);

    if (historyResponse && historyResponse.success) {
      console.log(
        "✅ SubscriptionUtil: Subscription history found:",
        historyResponse.data,
      );
      return historyResponse.data || [];
    } else {
      console.log("ℹ️ SubscriptionUtil: No subscription history found");
      return [];
    }
  } catch (error) {
    console.error(
      "💥 SubscriptionUtil: Error fetching subscription history:",
      error,
    );
    throw new Error(`Failed to load subscription history: ${error.message}`);
  }
};

/**
 * Checks if user is authenticated using UserContext data
 * @param {Object} user - User object from UserContext (loggedInUser or selectedUser)
 * @returns {boolean} True if user is authenticated, false otherwise
 */
export const isUserAuthenticated = (user) => {
  const authenticated = user && user.uuid && user.uuid.trim() !== "";
  console.log(
    "🔐 SubscriptionUtil: isUserAuthenticated -",
    authenticated
      ? "✅ Authenticated (from UserContext)"
      : "❌ Not authenticated",
  );
  return authenticated;
};

/**
 * Calculates the next payment date for a subscription
 * @param {string} startDate - The subscription start date
 * @param {string} paymentCycle - The payment cycle (MONTHLY, QUARTERLY, YEARLY, ANNUAL)
 * @param {Object} subscription - Optional subscription object to check if it's free
 * @returns {Date|null} The next payment date or null if no payment is required
 */
export const calculateNextPaymentDate = (
  startDate,
  paymentCycle,
  subscription = null,
) => {
  console.log("💰 SubscriptionUtil: Calculating next payment date:", {
    startDate,
    paymentCycle,
    subscription: subscription ? "provided" : "not provided",
  });

  // A $0 amount is ambiguous: it can be a genuinely free plan (no renewal) OR a
  // paid, auto-renewing plan that is $0 this cycle because of a 100%-off promo.
  // Only a genuinely free plan has no renewal date — short-circuiting for a
  // discounted/recurring paid plan wrongly renders "Next renewal: N/A".
  if (subscription) {
    const amountZero =
      Number(subscription.amount) === 0 || Number(subscription.price) === 0;
    const isRecurringPaid =
      Number(subscription.discountPercent) > 0 ||
      !!subscription.promoCode ||
      !!subscription.stripeSubscriptionId ||
      subscription.autoRenew === true;
    if (amountZero && !isRecurringPaid) {
      console.log(
        "💰 SubscriptionUtil: Free plan detected, no payment date needed",
      );
      return null;
    }
    // Prefer the authoritative renewal date from the backend/Stripe when present;
    // recomputing from startDate can drift for end-of-month billing anchors.
    const authoritative = subscription.nextBillingDate || subscription.endDate;
    if (authoritative) {
      const authoritativeDate = new Date(authoritative);
      if (!isNaN(authoritativeDate.getTime())) {
        return authoritativeDate;
      }
    }
  }

  if (!startDate) {
    console.log(
      "⚠️ SubscriptionUtil: No start date provided for payment calculation",
    );
    return null;
  }

  try {
    const start = new Date(startDate);
    console.log("📅 SubscriptionUtil: Parsed start date:", start);

    if (isNaN(start.getTime())) {
      console.error("❌ SubscriptionUtil: Invalid start date:", startDate);
      return null;
    }

    let nextPayment = new Date(start);
    const today = new Date();

    // Determine the payment interval based on payment cycle
    let monthsToAdd = 1; // Default to monthly
    switch (paymentCycle?.toUpperCase()) {
      case "MONTHLY":
        monthsToAdd = 1;
        break;
      case "QUARTERLY":
        monthsToAdd = 3;
        break;
      case "YEARLY":
      case "ANNUAL":
        monthsToAdd = 12;
        break;
      default:
        console.log(
          "⚠️ SubscriptionUtil: Unknown payment cycle, defaulting to monthly:",
          paymentCycle,
        );
        monthsToAdd = 1;
    }

    console.log("📊 SubscriptionUtil: Payment cycle details:", {
      paymentCycle,
      monthsToAdd,
    });

    // Keep adding payment periods until we get a future date
    while (nextPayment <= today) {
      nextPayment.setMonth(nextPayment.getMonth() + monthsToAdd);
    }

    console.log(
      "💰 SubscriptionUtil: Calculated next payment date:",
      nextPayment,
    );
    return nextPayment;
  } catch (error) {
    console.error(
      "💥 SubscriptionUtil: Error calculating next payment date:",
      error,
    );
    return null;
  }
};
